import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, BookOpen, Lock, Sparkles } from "lucide-react";
import { withMemberGate } from "@/lib/with-member-gate";
import { SHADOW_SECTIONS, sectionByKey } from "@/lib/shadow-journey";
import { ShadowSymbol } from "@/components/shadow/ShadowSymbol";
import { ShadowActivity } from "@/components/shadow/ShadowActivity";
import { SectionCheckIn } from "@/components/shadow/SectionCheckIn";
import { PauseGround } from "@/components/shadow/PauseGround";
import { useShadowJournal } from "@/components/shadow/useShadowJournal";

export const Route = createFileRoute("/shadow-journey")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Guided Shadow Journal — Recovery Trauma™" },
      {
        name: "description",
        content:
          "An illustrated, trauma-informed shadow work journal in twelve sections — masks, shame, protective parts, triggers and self-acceptance, explored at your own pace.",
      },
      { property: "og:title", content: "Guided Shadow Journal — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "An illustrated, trauma-informed shadow work journal in twelve sections, explored gently and privately at your own pace.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/shadow-journey" }],
  }),
  component: withMemberGate(ShadowJourneyPage, "Guided Shadow Journal"),
});

const GATE_KEY = "rt:gate:shadow-journey";
const LAST_KEY = "rt:shadow-journey:last";

function ShadowJourneyPage() {
  const [begun, setBegun] = useState(false);
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    try {
      if (window.localStorage.getItem(GATE_KEY) === "1") setBegun(true);
    } catch {
      /* storage unavailable */
    }
  }, []);

  const begin = () => {
    setBegun(true);
    try {
      window.localStorage.setItem(GATE_KEY, "1");
    } catch {
      /* noop */
    }
  };

  const open = (key: string | null) => {
    setActive(key);
    if (typeof window !== "undefined") {
      try {
        if (key) window.localStorage.setItem(LAST_KEY, key);
      } catch {
        /* noop */
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0b0a] text-[#f2ece1]">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:py-12">
        {!begun ? (
          <BeforeYouBeginScreen onBegin={begin} />
        ) : active ? (
          <SectionView sectionKey={active} onNavigate={open} />
        ) : (
          <JourneyMap onOpen={open} />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function BeforeYouBeginScreen({ onBegin }: { onBegin: () => void }) {
  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-8 text-center text-[#C9A227]">
        <ShadowSymbol symbol="door" className="mx-auto h-16 w-16" />
      </div>
      <h1 className="text-center font-serif text-3xl text-[#F3DFA2] sm:text-4xl">
        The Guided Shadow Journal
      </h1>
      <p className="mt-3 text-center text-sm leading-relaxed text-[#e8e2d8]/70">
        A private, illustrated journal in twelve parts — for meeting the parts
        of yourself that learned to hide, protect, adapt or survive.
      </p>

      <div className="mt-8 rounded-3xl border border-[#C9A227]/25 bg-[#161311] p-6 sm:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C9A227]">
          Before you begin
        </p>
        <p className="mt-3 text-sm leading-relaxed text-[#e8e2d8]/80">
          Some of these reflections may bring up difficult feelings. That is not
          a sign that something has gone wrong — but it does mean going slowly
          matters here.
        </p>
        <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-[#e8e2d8]/75">
          {[
            "You never have to complete a prompt.",
            "You can skip an exercise and come back to it — or never.",
            "You can stop a session at any point. Your writing is saved.",
            "A regulation tool is always one tap away.",
            "Nothing here diagnoses you, scores you or measures your healing.",
            "Everything you write stays private to your account.",
          ].map((t) => (
            <li key={t} className="flex gap-2.5">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C9A227]" />
              <span>{t}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6">
          <PauseGround />
        </div>

        <button
          type="button"
          onClick={onBegin}
          className="mt-6 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full bg-[#C9A227] px-6 text-sm font-semibold text-[#15120f] transition hover:bg-[#E8C766]"
        >
          I'm ready to begin <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function JourneyMap({ onOpen }: { onOpen: (key: string) => void }) {
  const { exploredSections, isLoading } = useShadowJournal();

  const currentKey = useMemo(() => {
    const next = SHADOW_SECTIONS.find((s) => !exploredSections.has(s.key));
    return next?.key ?? SHADOW_SECTIONS[SHADOW_SECTIONS.length - 1].key;
  }, [exploredSections]);

  return (
    <div>
      <header className="mb-8 text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#C9A227]/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C9A227]">
          <Sparkles className="h-3 w-3" /> Recovery Trauma™
        </span>
        <h1 className="mt-4 font-serif text-3xl text-[#F3DFA2] sm:text-4xl">
          The Guided Shadow Journal
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-[#e8e2d8]/70">
          Twelve sections, explored in any order you like. Progress here means
          only that you have explored a section — never that anything is
          finished, resolved or healed.
        </p>
        <p className="mt-3 text-xs text-[#e8e2d8]/45">
          {isLoading
            ? "Opening your private journal…"
            : `${exploredSections.size} of ${SHADOW_SECTIONS.length} sections explored`}
        </p>
      </header>

      <ol className="grid gap-3 sm:grid-cols-2">
        {SHADOW_SECTIONS.map((s) => {
          const explored = exploredSections.has(s.key);
          const current = s.key === currentKey && !explored;
          return (
            <li key={s.key}>
              <button
                type="button"
                onClick={() => onOpen(s.key)}
                className={[
                  "group flex w-full items-start gap-4 rounded-3xl border p-5 text-start transition",
                  current
                    ? "border-[#C9A227] bg-[#1d1813] shadow-[0_0_40px_-12px_rgba(201,162,39,0.55)]"
                    : explored
                      ? "border-[#C9A227]/35 bg-[#151211]"
                      : "border-white/5 bg-[#111010] hover:border-[#C9A227]/30",
                ].join(" ")}
              >
                <span
                  className={[
                    "shrink-0 transition",
                    current
                      ? "text-[#E8C766]"
                      : explored
                        ? "text-[#C9A227]/70"
                        : "text-[#e8e2d8]/25 group-hover:text-[#C9A227]/50",
                  ].join(" ")}
                >
                  <ShadowSymbol symbol={s.symbol} className="h-9 w-9" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C9A227]/70">
                    Section {s.index}
                  </span>
                  <span
                    className={[
                      "mt-0.5 block font-serif text-lg",
                      explored || current ? "text-[#F3DFA2]" : "text-[#e8e2d8]/60",
                    ].join(" ")}
                  >
                    {s.title}
                  </span>
                  <span className="mt-1 block text-sm leading-relaxed text-[#e8e2d8]/55">
                    {s.subtitle}
                  </span>
                  {explored ? (
                    <span className="mt-2 inline-block text-[11px] uppercase tracking-[0.16em] text-[#C9A227]/70">
                      Explored
                    </span>
                  ) : current ? (
                    <span className="mt-2 inline-block text-[11px] uppercase tracking-[0.16em] text-[#E8C766]">
                      Where you are
                    </span>
                  ) : (
                    <span className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] text-[#e8e2d8]/30">
                      <Lock className="h-3 w-3" /> Unexplored
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link
          to="/journal"
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-[#C9A227]/30 px-5 text-sm text-[#E8C766] hover:bg-[#C9A227]/10"
        >
          <BookOpen className="h-4 w-4" /> My Recovery Journal
        </Link>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function SectionView({
  sectionKey,
  onNavigate,
}: {
  sectionKey: string;
  onNavigate: (key: string | null) => void;
}) {
  const section = sectionByKey(sectionKey);
  if (!section) {
    return (
      <button type="button" onClick={() => onNavigate(null)} className="text-sm text-[#E8C766]">
        Back to the journey
      </button>
    );
  }

  const isFinal = section.index === SHADOW_SECTIONS.length;
  const next = SHADOW_SECTIONS.find((s) => s.index === section.index + 1) ?? null;
  const prev = SHADOW_SECTIONS.find((s) => s.index === section.index - 1) ?? null;

  return (
    <div
      className={
        isFinal
          ? "rounded-[2rem] bg-gradient-to-b from-[#1f1a14] via-[#171310] to-[#0d0b0a] p-1"
          : ""
      }
    >
      <div className={isFinal ? "p-4 sm:p-6" : ""}>
        <button
          type="button"
          onClick={() => onNavigate(null)}
          className="inline-flex min-h-[44px] items-center gap-2 text-sm text-[#e8e2d8]/60 hover:text-[#F3DFA2]"
        >
          <ArrowLeft className="h-4 w-4" /> The journey
        </button>

        <header className="mt-4 text-center">
          <div className={isFinal ? "text-[#F3DFA2]" : "text-[#C9A227]"}>
            <ShadowSymbol symbol={section.symbol} className="mx-auto h-14 w-14" />
          </div>
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C9A227]/70">
            Section {section.index} of {SHADOW_SECTIONS.length}
          </p>
          <h1 className="mt-1 font-serif text-3xl text-[#F3DFA2]">{section.title}</h1>
          <p className="mt-1 text-sm text-[#e8e2d8]/60">{section.subtitle}</p>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-[#e8e2d8]/75">
            {section.intro}
          </p>
        </header>

        {section.deeper ? (
          <div className="mt-6">
            <PauseGround />
          </div>
        ) : null}

        <div className="mt-6 space-y-5">
          {section.activities.map((a) => (
            <ShadowActivity key={a.key} sectionKey={section.key} activity={a} />
          ))}
        </div>

        <div className="mt-6">
          <SectionCheckIn sectionKey={section.key} />
        </div>

        {isFinal ? (
          <p className="mt-6 rounded-3xl border border-[#C9A227]/35 bg-[#C9A227]/10 p-6 text-center font-serif text-base leading-relaxed text-[#F3DFA2]">
            You were never asked to destroy your shadow.
            <span className="mt-3 block font-sans text-sm text-[#e8e2d8]/80">
              The purpose of this journey is to notice the parts of yourself
              that learned to hide, protect, adapt or survive — and begin
              meeting them with greater curiosity and compassion.
            </span>
            <span className="mt-3 block text-xs uppercase tracking-[0.2em] text-[#C9A227]">
              Recovery Trauma™
            </span>
          </p>
        ) : null}

        <nav className="mt-8 flex flex-wrap items-center justify-between gap-3">
          {prev ? (
            <button
              type="button"
              onClick={() => onNavigate(prev.key)}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-[#C9A227]/25 px-4 text-sm text-[#e8e2d8]/70 hover:text-[#F3DFA2]"
            >
              <ArrowLeft className="h-4 w-4" /> {prev.title}
            </button>
          ) : (
            <span />
          )}
          {next ? (
            <button
              type="button"
              onClick={() => onNavigate(next.key)}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[#C9A227] px-5 text-sm font-semibold text-[#15120f] hover:bg-[#E8C766]"
            >
              Continue to {next.title} <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onNavigate(null)}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-[#C9A227]/40 px-5 text-sm text-[#E8C766]"
            >
              Return to the journey
            </button>
          )}
        </nav>
      </div>
    </div>
  );
}
